import React from 'react';
import styled from '@emotion/styled';
import { useLocation } from 'react-router';

import { useDeviceInfo, useSelector } from '~Hooks';
import Toast from './Toast';

const Wrapper = styled.div<{ top: string; right: string; bottom: string; isPhone?: boolean }>`
  display: flex;
  width: ${({ isPhone }) => (isPhone ? '100%' : 'auto')};
  position: fixed;
  top: ${({ top }) => top};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
  z-index: 999;
  gap: 4px;
`;

const ToastWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const Toasts = () => {
  const location = useLocation();
  const toasts = useSelector((state) => state.toasts);
  const deviceInfo = useDeviceInfo();

  let positionObj = { top: '16px', right: '16px', bottom: 'auto' };

  // Toasts need to clear the notch on phones
  if (deviceInfo?.isPhone) {
    positionObj = { top: '10%', right: 'auto', bottom: 'auto' };
  }

  // In tables view, position toasts at the bottom
  const isTables = location.pathname.includes('tables');
  if (location.pathname.includes('tables')) {
    positionObj = { top: 'auto', right: '415px', bottom: '70px' };
  }

  const isPOS = location.pathname.includes('pos');
  // In pos view, position toasts at the bottom
  if (isPOS) {
    positionObj = { top: 'auto', right: '343px', bottom: '78px' };
  }

  const hasBottomPlacement = isPOS || isTables;

  // Toasts that asked for 'top' (e.g. from a modal's add button) skip the route-based position,
  // which on POS/Tables would cover the modal's bottom bar. Phones are already top-placed.
  const topPositionObj = deviceInfo?.isPhone
    ? positionObj
    : { top: '16px', right: '16px', bottom: 'auto' };
  const topToasts = toasts.filter((toast) => toast.placement === 'top');
  const defaultToasts = toasts.filter((toast) => toast.placement !== 'top');

  /** Renders one stack of toasts, newest in front, older ones offset and shrunk behind it */
  const renderStack = (
    stack: typeof toasts,
    position: typeof positionObj,
    fromBottom: boolean,
    key: string,
  ) => {
    if (!stack.length) return null;

    return (
      <Wrapper
        key={key}
        top={position.top}
        right={position.right}
        bottom={position.bottom}
        isPhone={deviceInfo?.isPhone}>
        {[...stack].reverse().map((toast, index) => {
          const max = 24;
          const offset = index * 8 > max ? max : index * 8;
          const scale = 1 - index * 0.05;

          return (
            <ToastWrapper
              key={'toast-wrapper-' + toast.id}
              style={{
                top: `${fromBottom ? 'auto' : offset + 'px'}`, // Push up each toast by 2px
                bottom: `${fromBottom ? offset + 'px' : 'auto'}`,
                transform: `scale(${scale})`, // Shrink the previous toasts gradually
                zIndex: 1000 - index, // Control the stacking order
              }}>
              <Toast key={toast.id} {...toast} />
            </ToastWrapper>
          );
        })}
      </Wrapper>
    );
  };

  return (
    <>
      {renderStack(defaultToasts, positionObj, hasBottomPlacement, 'default-toasts')}
      {renderStack(topToasts, topPositionObj, false, 'top-toasts')}
    </>
  );
};

export default Toasts;
